import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProjectView } from '@lemoncloud/codes-goods-api';

import { projectsKeys } from '@codes/project';
import { PaginationType } from '@codes/shared';

import { useUpdateQueryData } from './useUpdateQueryData';
import { useDialogStore, useSelectedStore } from '../stores';

interface UseCreateProjectDialogProps {
    onComplete?: (newProject: ProjectView) => void;
    onClose?: () => void;
}

export const useCreateProjectDialog = ({ onComplete, onClose }: UseCreateProjectDialogProps) => {
    const { isProjectDialogOpen, setProjectDialog } = useDialogStore(state => state);
    const { setProject, workspace } = useSelectedStore(state => state);

    const updateQueryData = useUpdateQueryData<PaginationType<ProjectView[]>>();
    const { t } = useTranslation();

    const handleComplete = async (newProject: ProjectView) => {
        setProjectDialog(false);

        setProject(newProject);
        updateQueryData({
            queryKey: projectsKeys.list({ workspaceId: workspace?.id, params: { limit: 50, page: 0 } }),
            updater: oldData => {
                if (!oldData) {
                    return { total: 1, data: [newProject], page: 0 };
                }

                return {
                    ...oldData,
                    data: [newProject, ...oldData.data],
                    list: [newProject, ...oldData.data],
                    total: (oldData.total || 0) + 1,
                };
            },
        });
        toast({
            description: t('project.create.projectCreationCompleted'),
        });

        onComplete?.(newProject);
    };

    const handleClose = () => {
        onClose?.();
        setProjectDialog(false);
    };

    return { isProjectDialogOpen, setProjectDialog, handleComplete, handleClose };
};
